import { ProgressSpinner } from 'primereact/progressspinner';
import { Navigate } from 'react-router-dom';

import Dashboard from '@/Layout/Dashboard.jsx';
import { usePermissionsForUser, usePermissionsList } from '@/modules/access/hooks/permissions';

import { PAYMENTS_ROUTE, ROUTE_ALLOWED_ROLES, UserRole } from './constants';
import { useIsInsolvent } from './hooks/useIsInsolvent';
import { resolveRouteAccess } from './routeAccess';

export function ProtectedRoute({
  children,
  path,
  redirectTo = '/login',
  withPaymentProtection = true,
}) {
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  const permissions = usePermissionsForUser(null, { enabled: !isAdmin });
  const fullPermissions = usePermissionsList();

  if (!path) {
    return <Navigate to={redirectTo} />;
  }

  const isAuthenticated = localStorage.getItem('token');
  const role = localStorage.getItem('role');

  if (isAdmin) return children;

  if (
    path === '/casos/nuevo' &&
    role === UserRole.MEDICAL_CENTER &&
    localStorage.getItem('can_create_case') === 'true'
  ) {
    return children;
  }

  const roles = ROUTE_ALLOWED_ROLES[path] || [];

  const fullPermsLoading = fullPermissions.isLoading;
  const userPermsLoading = !isAdmin && permissions.isLoading;

  if (fullPermsLoading || userPermsLoading) {
    return (
      <Dashboard>
        <ProgressSpinner />
      </Dashboard>
    );
  }

  const pathPerm = `dashboard:view:${path}`;
  const permissionExists = fullPermissions.data.map(fp => fp.codename).includes(pathPerm);

  const canAccess = resolveRouteAccess({
    path,
    allowedRoles: roles,
    role,
    isAdmin,
    isAuthenticated: Boolean(isAuthenticated),
    permissionExists,
    userPermissions: permissions.data || [],
  });

  if (!canAccess) {
    return <Navigate to={redirectTo} />;
  }

  return withPaymentProtection ? (
    <PaymentProtectedRoute>{children}</PaymentProtectedRoute>
  ) : (
    children
  );
}

export function PaymentProtectedRoute({ children }) {
  const role = localStorage.getItem('role');
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  const insolvent = useIsInsolvent({
    enabled: [UserRole.MEDICAL_CENTER, UserRole.SUB_ADMINISTRATOR].includes(role) && !isAdmin,
    initialData: { is_insolvent: false },
  });

  if (insolvent.data.is_insolvent) {
    return <Navigate to={PAYMENTS_ROUTE} state={{ showInsolventDialog: true }} />;
  } else {
    return children;
  }
}
